import { FileCheck2, FilePlus2, Loader } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import Spacing from '../Spacing';
import { notify, datePickerFormat, usPhoneFormat, getPractice, exchangeDate } from "../../helpers";
import { createBillingInvoice, updateInvoice } from '../../services/billing';
import { Config } from '../../Config';
import { Drawer } from 'vaul';

export default function CreateInvoiceModal(props) {
  const {    
    reloadData = () => {},
    account_id,
    location_id,
    invoiceCreate = {},
    setInvoiceCreate = () => {},
    type,
    invoiceEditData = {},
    practiceBillingData = {},
    previousScrollPosition = 0,
  } = props;

  const defaultValues = {
    account_id:account_id,
    billing_date: invoiceEditData?.billing_date ? exchangeDate(invoiceEditData.billing_date) : datePickerFormat(new Date()),
    coding_done: invoiceEditData?.coding_done ? invoiceEditData.coding_done : null,
    charting_done: invoiceEditData?.charting_done ? invoiceEditData.charting_done : null,
    coding_amount: 0,
    charting_amount: 0,
    paid_amount: invoiceEditData?.paid_amount ? invoiceEditData.paid_amount : 0,
  }

  const [formData, setFormData] = useState(defaultValues);
  const [createInvoiceLoader, setCreateInvoiceLoader] = useState(false);
  const dropdownRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const parsedValue = Math.max(0, parseInt(value, 10) || 0);
    let updatedFields = {};

    if (name === "coding_done") {
      updatedFields = {
        coding_done: parsedValue,
        coding_amount: parsedValue * (practiceBillingData?.coding_billing_price || 0),
      };
    } else if (name === "charting_done") {
      updatedFields = {
        charting_done: parsedValue,
        charting_amount: parsedValue * (practiceBillingData?.charting_billing_price || 0),
      };
    }

    const newCodingAmount = updatedFields.coding_amount ?? formData.coding_amount;
    const newChartingAmount = updatedFields.charting_amount ?? formData.charting_amount;
    const paidAmount = newCodingAmount + newChartingAmount;

    setFormData({
      ...formData,
      ...updatedFields,
      paid_amount: paidAmount
    });
  };

  const handleChangeDate = (name, value) => {
    setFormData({
      ...formData,
      [name]: datePickerFormat(value),
    });  
  };

  useEffect(() => {
    if(type === 'edit'){
      setFormData({
        ...formData,
        id: invoiceEditData.id,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, invoiceEditData]);

  const canSubmit = () => {
    return formData.charting_done > 0 || formData.coding_done > 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit()) {
      notify('error', "At least one value (Charting or Coding) must be greater than 0.");
      return;
    }

    const customizedData = {
      ...formData,
      coding_done: formData?.coding_done ?? 0,
      charting_done: formData?.charting_done ?? 0,
    };

    e.target.disabled = true;
    setCreateInvoiceLoader(true);
    const { charting_amount, coding_amount, paid_amount, ...submittedData } = customizedData;

    let response;
    if(type === 'edit'){
      response = await updateInvoice(customizedData);
    } else {
      response = await createBillingInvoice(submittedData);
    }

    if (response?.detail) {
      notify('error', response?.detail);
    } else {
      if(response?.data?.status === 'success'){
        notify("success", response?.data?.message);
        setFormData(defaultValues);
        reloadData();     
        handleDrawerClose();
      } else {
        notify("error", response?.data?.message);
      }
    } 
    e.target.disabled = false;
    setCreateInvoiceLoader(false);
  };

  const handleDrawerClose = () => {
    setInvoiceCreate(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
    <Drawer.Root open={invoiceCreate} onOpenChange={handleDrawerClose}>
      <Drawer.Portal>
        <Drawer.Overlay className="docpro_drawer_overlay"/>
        <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
          <Drawer.Title className="sr-only">Form View</Drawer.Title>
          <Drawer.Description className="sr-only">View a form details</Drawer.Description>
          <div className={`docpro_modal_wrap docpro_style_1 docpro_modal_lg`}>

            <div className="docpro_modal" style={{ position: 'unset' }}>
              <div className="docpro_signout_wrap">
                <form onSubmit={handleSubmit}> 
                  <div className="docpro_signout flex custom-scroll" style={{ minHeight: '100vh', flex: '1 1', flexDirection: 'column' }}>
                    <div className='w-full'>
                      <div className="docpro_signout_header mb-0" style={{ height: '70px', borderBottom: 0 }}>
                        <div className="docpro_signout_logo">
                          <img 
                            src="/images/logo.svg" 
                            alt="Logo" 
                            style={{ height: "36px" }}
                          />
                        </div>
                      </div>
                      <div className="docpro_signout_input invoice_input cs_flex_1 items-center">
                        <label className="docpro_fw_600 docpro_primary_color">
                          Billing date:
                        </label>
                        <DatePicker
                          type="text"
                          name="billing_date"
                          style={{ maxWidth: "100px" }}
                          selected={formData?.billing_date}
                          onChange={(date) =>
                            handleChangeDate("billing_date", date)
                          }
                        />
                      </div>
                      <Spacing lg="30" sm="30" />
                      <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_2 input">
                        <div className="docpro_grid_item">
                          <p className="docpro_fs_24 docpro_ternary_color">From:</p>
                          <p className="docpro_mb_0 docpro_fs_16"><b>{Config?._AppName}</b></p>
                          <p className="docpro_mb_0">{Config?._AppAddress}</p>
                          <p className="docpro_mb_0">{Config?._AppPhone}</p>
                          <p className="docpro_mb_0">{Config?._AppEmail}</p>
                        </div>

                        <div className="docpro_grid_item">
                          <p className="docpro_fs_24 docpro_ternary_color">To:</p>
                          <p className="docpro_mb_0 docpro_fs_16"><b>{getPractice("account_name")}</b></p>
                          <p className="docpro_mb_0">{getPractice("location_name")}</p>
                        </div>
                      </div>
                      <Spacing lg="35" sm="35" />
                      <h4 className="docpro_mb_0 docpro_fs_18 mb-1">Invoice Details</h4>
                      <Spacing lg="15" sm="15" />
                      <div className="docpro_table_responsive">
                        <table className="docpro_mb_0 invoice_table">
                          <tbody>
                            <tr>
                              <th>
                                <b>Description</b>
                              </th>
                              <th className="text-right" style={{ width: '200px' }}>
                                <b>Total Done</b>
                              </th>
                              <th className="text-right" style={{ width: '150px' }}>
                                <b>Charge</b>
                              </th>
                            </tr>
                            <tr>
                              {/* <td>{practiceBillingData?.coding_billing_name} - ${practiceBillingData?.coding_billing_price?.toFixed(2) } each</td> */}
                              <td>{practiceBillingData?.coding_billing_name} {practiceBillingData?.coding_billing_off !==  "DEFAULT" && (<small className='docpro_red_color'>({practiceBillingData?.coding_billing_off})</small>) }</td>
                              <td className="text-right input-align">
                                <input 
                                  type="number"
                                  className="docpro_form_field text-right"
                                  name='coding_done'
                                  onChange={handleChange}
                                  defaultValue={formData?.coding_done}
                                  min="0"
                                  step="1"
                                  // onKeyDown={(e) => {
                                  //   if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                  //     e.preventDefault();
                                  //   }
                                  // }}
                                />
                              </td>
                              <td className="text-right">
                                ${formData?.coding_amount?.toFixed(2)}
                                <br/>
                                <small className='docpro_ternary_color'>${practiceBillingData?.coding_billing_price?.toFixed(2) } each</small>
                              </td>
                            </tr>
                            <tr>
                              {/* <td>{practiceBillingData?.charting_billing_name} - ${practiceBillingData?.charting_billing_price?.toFixed(2) } each</td> */}
                              <td>{practiceBillingData?.charting_billing_name} {practiceBillingData?.charting_billing_off !==  "DEFAULT" && (<small className='docpro_red_color'>({practiceBillingData?.charting_billing_off})</small>) }</td>
                              <td className="text-right input-align">
                                <input 
                                  type="number"
                                  className="docpro_form_field text-right"
                                  name='charting_done'
                                  onChange={handleChange}
                                  defaultValue={formData?.charting_done}
                                  min="0"
                                  step="1"
                                  // onKeyDown={(e) => {
                                  //   if (e.key === '.' || e.key === '-' || e.key === 'e') {
                                  //     e.preventDefault();
                                  //   }
                                  // }}
                                />
                              </td>
                              <td className="text-right" style={{ borderTop: '1px solid var(--border)' }}>
                                ${formData?.charting_amount?.toFixed(2)}
                                <br/>
                                <small className='docpro_ternary_color'>${practiceBillingData?.charting_billing_price?.toFixed(2) } each</small>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={3} style={{ padding: '25px 0 8px' }}><hr /></td>
                            </tr>
                            <tr>
                              <td className="text-right" colSpan={2}>
                                <b>Total</b>
                              </td>
                              <td className="text-right">
                                <b>${formData?.paid_amount?.toFixed(2)}</b>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className='text-right'>
                      <Spacing lg="50" sm="50" />
                      <button
                        type="submit"
                        style={{ borderRadius: '4px' }}
                        className="docpro_bordered_btn size_lg docpro_fw_600 docpro_white_color docpro_filed_btn"
                        disabled={createInvoiceLoader ? true : false}
                      >
                        {type !== 'edit' ?
                          <>
                            <FilePlus2 width={20} height={20} />
                            <span>Create Invoice</span>
                            {createInvoiceLoader && (
                              <span className="docpro_loading">
                                <Loader width={20} height={20} />
                              </span>
                            )}
                          </>
                        :
                          <>
                            <FileCheck2 width={20} height={20} />
                            <span>Update Invoice</span>
                          </>
                        }
                      </button>
                    </div>
                  </div>
                </form>

                <div className="docpro_invoice_btns docpro_hide_print">
                  <button
                    type='button'
                    className="docpro_invoice_btn docpro_color3 close_button"
                    onClick={() => {handleDrawerClose();}}
                  >
                    <span className="docpro_btn_icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </span>
                    <span className="docpro_btn_text">Close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
